import type { JSXElementConstructor } from "react";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Property } from "csstype";

import { IS_APP } from "@/__main__/constants.mjs";
import router from "@/__main__/router.mjs";
import Container from "@/shared/ContentContainer.jsx";
import {
  cssTitleTransition,
  Header,
  PageDescription,
} from "@/shared/PageHeader.style.jsx";
import type { PageHeaderLink } from "@/shared/PageHeaderLinks.jsx";
import PageHeaderLinks from "@/shared/PageHeaderLinks.jsx";
import ShareButton from "@/shared/ShareButton.jsx";
import { useInertTransitionParent } from "@/shared/Transition.jsx";
import { useHeaderHeight } from "@/util/app-query.mjs";
import { classNames } from "@/util/class-names.mjs";
import { reactNodeKey } from "@/util/react.mjs";
import { useRouteMetadata } from "@/util/router-hooks.mjs";

declare module "@/__main__/router.mjs" {
  interface MetaExt {
    // Displays this subtitle on web only
    subtitle?: Translation | true;
  }
}

type ImageProp = Either<
  [
    // ImageComponent will not be wrapped, sized, and displayed in a circle like icon will
    {
      ImageComponent: React.ReactNode;
    },
    {
      image: string;
      imageLink?: string;
      imageAlt?: string;
      imageStyles: React.CSSProperties;
    },
  ]
>;

type AccentProps = Either<
  [
    {
      imageAccent: React.ReactNode;
    },
    { accentText: string },
  ]
>;

export type PageHeaderProps = Partial<ImageProp & AccentProps> & {
  borderColor?: Property.BorderColor;
  title:
    | Translation
    | string
    | JSXElementConstructor<unknown>
    | React.ReactNode;
  titleLink?: string;
  rightTitle?: React.ReactNode;
  underTitle?: React.ReactNode;
  links?: PageHeaderLink[];
  linksLoading?: boolean;
  aside?: React.ReactNode;
  children?: React.ReactNode;
  counterModeFn?: () => { url: string; class: string };
  imageRound?: boolean;
  navLabel?: string;
  icon?: React.JSX.Element;
} & Omit<React.ComponentPropsWithoutRef<"div">, "title">;

function findComponent(FC: React.FC, node: React.ReactNode): boolean {
  if (!node) return false;

  if (typeof node === "object") {
    if (Symbol.iterator in node) {
      return [...node].some((n) => findComponent(FC, n));
    }

    if (node.type === FC) {
      return true;
    }

    return findComponent(FC, node.props?.children);
  }

  return false;
}

const PageHeader = ({
  ImageComponent,
  image,
  imageStyles,
  imageLink,
  imageAlt,
  imageRound,
  borderColor,
  accentText,
  imageAccent,
  title,
  titleLink,
  rightTitle,
  underTitle,
  links,
  linksLoading,
  aside,
  children,
  counterModeFn = null,
  className,
  navLabel = null,
  ...props
}: PageHeaderProps) => {
  const metadata = useRouteMetadata();

  const subtitle =
    metadata?.subtitle === true ? metadata?.description : metadata?.subtitle;

  const alignmentClass = "sidebar-align";
  const alignmentChild = "sidebar-align-child";

  const transitionBase = (node) =>
    node.classList.add(cssTitleTransition(router.isBackwards));

  const titleTransition = useInertTransitionParent(
    useMemo(
      () => ({
        onLeave: transitionBase,
        onEnter: transitionBase,
      }),
      [],
    ),
  );

  const childRef = useRef(null);

  useEffect(() => {
    const currentNode = childRef.current;
    titleTransition(currentNode);
  }, [childRef, titleTransition]);

  const headerHeight = useHeaderHeight();

  const { t } = useTranslation();

  const Title =
    typeof title === "function"
      ? title
      : () => (Array.isArray(title) ? t(...title) : title);

  const titleRender = <Title />;
  const descriptionRender = !IS_APP && subtitle && (
    <PageDescription
      className="type-subtitle--semi shade1"
      data-sticky
      data-sticky-height="0"
    >
      {t(...subtitle)}
    </PageDescription>
  );

  const key = reactNodeKey(titleRender);

  const imageSizeStyle = IS_APP ? "var(--sp-14)" : "var(--sp-18)";

  // this might be temporary? we will need to see how our designs evolve
  const withShareButton = !findComponent(ShareButton, underTitle);

  return (
    <>
      <Header
        ref={childRef}
        data-sticky
        data-sticky-height={headerHeight}
        data-sticky-z="-5"
        {...props}
        {...classNames(
          className,
          "page-header",
          !Array.isArray(links) || !links.length ? "empty-links" : "",
        )}
      >
        <Container key={key} className={`${alignmentClass} ${alignmentChild}`}>
          <div className="inner-container">
            <div
              className="container left"
              style={{
                "--alignment": descriptionRender ? "start" : "center",
                "--gap": IS_APP ? "var(--sp-4)" : "var(--sp-5)",
              }}
            >
              {ImageComponent ? (
                ImageComponent
              ) : image?.length ? (
                <div
                  className="header-image--outer"
                  style={{ "--img-radius": imageRound && "50%" }}
                >
                  {imageLink ? (
                    <a
                      href={imageLink}
                      className="header-image--inner"
                      style={{
                        "--border-color": borderColor,
                        "--header-img-size": imageSizeStyle,
                      }}
                    >
                      <img
                        src={image}
                        width={72}
                        height={72}
                        className="header-image--img"
                        alt={imageAlt}
                        style={imageStyles || {}}
                      />
                    </a>
                  ) : (
                    <div
                      className="header-image--inner"
                      style={{
                        "--border-color": borderColor,
                        "--header-img-size": imageSizeStyle,
                      }}
                    >
                      <img
                        src={image}
                        width={72}
                        height={72}
                        className="header-image--img"
                        alt={imageAlt}
                        style={imageStyles || {}}
                      />
                    </div>
                  )}

                  {imageAccent ? (
                    <span className="accent-icon">{imageAccent}</span>
                  ) : accentText ? (
                    <span className="type-caption--bold accent-pill">
                      {accentText}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="flex column gap-sp-1">
                <h1 {...classNames("flex type-page-header")}>
                  {titleLink ? (
                    <a href={titleLink}>{titleRender}</a>
                  ) : (
                    titleRender
                  )}
                  {rightTitle}
                  {withShareButton && (
                    <>
                      <span className="shade4 interpunct" />
                      <ShareButton className="type-subtitle--semi shade1" />
                    </>
                  )}
                </h1>
                {(underTitle || descriptionRender || withShareButton) && (
                  <div className="flex align-center gap-sp-2 type-caption--semi shade1">
                    {underTitle}
                    {underTitle && !aside && descriptionRender && (
                      <span className="shade4 interpunct" />
                    )}
                    {!aside && descriptionRender}
                  </div>
                )}
                {aside && descriptionRender}
              </div>
            </div>
            {aside && <div className="right">{aside}</div>}
          </div>
        </Container>
      </Header>

      {children}

      <PageHeaderLinks
        links={links}
        linksLoading={linksLoading}
        counterModeFn={counterModeFn}
        navLabel={navLabel}
      />
    </>
  );
};

export default PageHeader;
